import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { Heading, Text } from "suomifi-ui-components";
import {
  IconHelpdesk,
  IconConversation,
  IconChatBubbles,
  IconMessageSent,
  IconTeam,
  IconCollaboration,
  IconManLaptop,
  IconChartStatistic,
  IconChartPie,
  IconCatalog,
  IconFileCabinet,
} from "suomifi-icons";
import type { ComponentType } from "react";

import { voiNahda } from "../utils/oikeudet";
import OikeusBanneri from "../components/OikeusBanneri";
import { useTodellinenRooli } from "../hooks/useTodellinenRooli";
import { useGetTukipyynnotQuery } from "../api/backendApi";
import { useTranslation } from "react-i18next";

type IllustrativeIcon = ComponentType<{
  baseColor?: string;
  highlightColor?: string;
  className?: string;
  ariaLabel?: string;
  style?: React.CSSProperties;
}>;

interface KpiProps {
  numero: number;
  label: string;
  taustaToken: "alert" | "brand" | "tertiary";
  /** 8 pattern-ikonia 4×2 gridiin, suorassa, ei kiertoa. */
  patternIkonit: IllustrativeIcon[];
  toiminto?: { teksti: string; fn: () => void };
}

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.l};
`;

const HeroBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xxs};
`;

const SubLine = styled(Text)`
  color: ${({ theme }) => theme.colors.depthDark1};
`;

const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${({ theme }) => theme.spacing.s};
`;

/* KpiCard on koko kortin laajuinen klikkausalue. Toteutetaan ns. link-
   overlay -patternilla: KpiActionin ::after laajenee koko kortin yli ja
   kantaa klikin → yksi tab-stop, yksi accessible name, ei tarvita role/
   tabindex/aria. Hover-tilassa hienovarainen drop shadow, pressed-tilassa
   shadow ja mikroanimaatiot palaavat lähtötilaan. Focus-rengas siirretään
   koko kortin ympärille :has()-selectorilla, jotta keyboard-fokus näkyy
   kortin laajuudella eikä pelkän painikkeen tekstin ympärillä. */
const KpiCard = styled.div`
  position: relative;
  background: ${({ theme }) => theme.colors.whiteBase};
  border: 1px solid ${({ theme }) => theme.colors.depthLight1};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: box-shadow 180ms ease;

  &:hover {
    box-shadow: 0 4px 12px hsla(0, 0%, 13%, 0.1);
  }

  &:active {
    box-shadow: none;
  }

  &:has(button:focus-visible) {
    outline: 2px solid ${({ theme }) => theme.colors.highlightBase};
    outline-offset: 2px;
  }
`;

/* Värialue: pattern täyttää koko alueen, numero on keskellä.
   pointer-events: none — koko yläosa on dekoratiivinen, klikit menevät
   läpi KpiAction::after-overlay-pseudolle (link-overlay -pattern). Ilman
   tätä Numeron z-index:1 nappaisi klikin itselleen ja näyttäisi
   tekstikursorin pointer-kursorin sijaan. */
const ValueArea = styled.div<{ $tausta: "alert" | "brand" | "tertiary" }>`
  position: relative;
  pointer-events: none;
  background: ${({ theme, $tausta }) => {
    if ($tausta === "alert") return theme.colors.alertBase;
    if ($tausta === "tertiary") return theme.colors.accentTertiaryDark1;
    return theme.colors.brandBase;
  }};
  height: 11rem;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

/* Pattern-kerros: DS:n kuvitusikonit 4×2-gridissä koko värialueen taustalla.
   Värit valkoinen + keskivaalea harmaa luovat hienovaraisen kahden sävyn
   erottelun pattern-opacityn kautta sekoittuessaan väritaustaan. */
const PatternLayer = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-template-rows: repeat(2, 1fr);
  align-items: center;
  justify-items: center;
  padding: ${({ theme }) => theme.spacing.xs};
  opacity: 0.24;

  & > svg {
    width: 4rem;
    height: 4rem;
  }
`;

/* Numero saa taustakseen taustavärinen radial gradient -"halon",
   joka erottaa numeron pattern-tekstuurista pehmeästi. Halo feidaa
   keskeltä ulospäin transparenttiin → ei jyrkkä ympyrä.
   Hoverissa numero kasvaa hienovaraisesti (1.04), pressed-tilassa
   palaa alkuperäiseen kokoonsa. Transform ei aiheuta layout shiftiä.

   EI z-index:1 — luo oman stacking-contextin joka aiheutti että numero
   näkyi modaalien päällä. DOM-järjestys riittää: Numero renderöidään
   PatternLayer:n jälkeen, joten se on luonnostaan päällä. */
const Numero = styled.span<{ $tausta: "alert" | "brand" | "tertiary" }>`
  position: relative;
  color: ${({ theme }) => theme.colors.whiteBase};
  font-weight: 800;
  line-height: 1;
  font-size: 5.5rem;
  font-variant-numeric: tabular-nums;
  padding: ${({ theme }) => theme.spacing.l} ${({ theme }) => theme.spacing.xxl};
  transition: transform 180ms ease;
  background: ${({ theme, $tausta }) => {
    const taustavari =
      $tausta === "alert"
        ? theme.colors.alertBase
        : $tausta === "tertiary"
        ? theme.colors.accentTertiaryDark1
        : theme.colors.brandBase;
    return `radial-gradient(ellipse at center, ${taustavari} 30%, transparent 75%)`;
  }};

  ${KpiCard}:hover & {
    transform: scale(1.04);
  }

  ${KpiCard}:active & {
    transform: scale(1);
  }
`;

const KpiBody = styled.div`
  padding: ${({ theme }) => theme.spacing.m};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const KpiLabel = styled.div`
  font: ${({ theme }) => theme.typography.bodySemiBold};
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.blackBase};
`;

/* KpiAction on aito <button> jossa onClick — sen ::after laajentaa
   klikkialueen koko kortin yli (link-overlay -pattern). Focus-rengas
   siirretty pois itse painikkeesta KpiCardiin (:has(button:focus-visible)),
   jotta keyboard-fokus näkyy koko kortin laajuudessa. */
const KpiAction = styled.button`
  background: transparent;
  border: 0;
  padding: 0;
  text-align: left;
  cursor: pointer;
  font: ${({ theme }) => theme.typography.bodySemiBoldSmall};
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.highlightBase};
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;

  &:hover {
    text-decoration: underline;
    color: ${({ theme }) => theme.colors.highlightDark1};
  }

  &:focus-visible {
    outline: none;
  }

  &::after {
    content: "";
    position: absolute;
    inset: 0;
  }
`;

/* Chevron liukuu hoverissa 3px oikealle (navigation-card-mikroanimaatio),
   pressed-tilassa palaa lähtöpaikkaan. */
const KpiArrow = styled.span`
  font-weight: 700;
  color: ${({ theme }) => theme.colors.alertBase};
  display: inline-block;
  transition: transform 180ms ease;

  ${KpiCard}:hover & {
    transform: translateX(3px);
  }

  ${KpiCard}:active & {
    transform: translateX(0);
  }
`;

/* Pattern-värit:
 * - HIGHLIGHT = valkoinen (korvaa DS:n oranssin)
 * - BASE = keskivaalea harmaa (korvaa DS:n siniharmaan)
 * Tummempi BASE-arvo varmistaa että valkoiset ja harmaat osat erottuvat
 * eri sävyisinä myös kun container-opacity sekoittaa pattern värit taustaan. */
const PATTERN_BASE = "#A4ABAF"; // depthBase — keskivaalea harmaa, erottuu valkoisesta
const PATTERN_HIGHLIGHT = "#FFFFFF"; // whiteBase — täysin valkoinen

function KpiKortti({
  numero,
  label,
  taustaToken,
  patternIkonit,
  toiminto,
}: KpiProps) {
  return (
    <KpiCard>
      <ValueArea $tausta={taustaToken}>
        <PatternLayer aria-hidden>
          {patternIkonit.map((Icon, i) => (
            <Icon
              key={i}
              baseColor={PATTERN_BASE}
              highlightColor={PATTERN_HIGHLIGHT}
              ariaLabel=""
            />
          ))}
        </PatternLayer>
        <Numero $tausta={taustaToken}>{numero}</Numero>
      </ValueArea>
      <KpiBody>
        <KpiLabel>{label}</KpiLabel>
        {toiminto && (
          <KpiAction onClick={toiminto.fn} type="button">
            <KpiArrow>›</KpiArrow> {toiminto.teksti}
          </KpiAction>
        )}
      </KpiBody>
    </KpiCard>
  );
}

export default function Etusivu() {
  const { t, i18n } = useTranslation(["etusivu", "common"]);
  const navigate = useNavigate();

  const {
    kayttaja: virkailija,
    todellinenRooli,
    isLoading,
    error,
  } = useTodellinenRooli();
  const { data: tukipyynnot = [] } = useGetTukipyynnotQuery();

  const avoimet = tukipyynnot.filter((t) => t.tila === "avoin").length;
  const kasittelyssa = tukipyynnot.filter(
    (t) => t.tila === "käsittelyssä",
  ).length;
  const yhteensa = tukipyynnot.length;

  const dateLocale = i18n.language === "en" ? "en-GB" : "fi-FI";

  const paiva = new Date().toLocaleDateString(dateLocale, {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  return (
    <Page>
      <HeroBlock>
        <Heading variant="h1">
          {t("tervetuloa", { nimi: virkailija?.name?.givenName ?? "" })}
        </Heading>
        <SubLine>
          {t(`roolit.${todellinenRooli?.id ?? "mukautettu"}`, { ns: "common" })}{" "}
          · {paiva}
        </SubLine>
      </HeroBlock>

      {voiNahda("navi.tukipyynnot", todellinenRooli?.profiili) && (
        <KpiGrid>
          <KpiKortti
            numero={avoimet}
            label={t("kpi.avoimet")}
            taustaToken="alert"
            patternIkonit={[
              IconConversation,
              IconChatBubbles,
              IconMessageSent,
              IconHelpdesk,
              IconHelpdesk,
              IconMessageSent,
              IconChatBubbles,
              IconConversation,
            ]}
            toiminto={{
              teksti: t("kpi.nayta.avoimet"),
              fn: () => navigate("/tukipyynnot?tila=avoin"),
            }}
          />
          <KpiKortti
            numero={kasittelyssa}
            label={t("kpi.kasittelyssa")}
            taustaToken="brand"
            patternIkonit={[
              IconTeam,
              IconCollaboration,
              IconManLaptop,
              IconConversation,
              IconConversation,
              IconManLaptop,
              IconCollaboration,
              IconTeam,
            ]}
            toiminto={{
              teksti: t("kpi.nayta.kasittelyssa"),
              fn: () =>
                navigate(
                  `/tukipyynnot?tila=${encodeURIComponent("käsittelyssä")}`,
                ),
            }}
          />
          <KpiKortti
            numero={yhteensa}
            label={t("kpi.yhteensa")}
            taustaToken="tertiary"
            patternIkonit={[
              IconChartStatistic,
              IconChartPie,
              IconCatalog,
              IconFileCabinet,
              IconFileCabinet,
              IconCatalog,
              IconChartPie,
              IconChartStatistic,
            ]}
            toiminto={{
              teksti: t("kpi.nayta.kaikki"),
              fn: () => navigate("/tukipyynnot"),
            }}
          />
        </KpiGrid>
      )}

      <OikeusBanneri />
    </Page>
  );
}
